// FLAG:useDocuAi — expense or revenue? The SERVER makes the authoritative decision
// (`api/src/services/receiptReviewCaseService.ts::resolveReviewedDocumentTarget`) — it creates and
// books the receipt, then reports the target in the save preview (`getReviewSaveEffects`). This
// module is exclusively the LIVE projection of the same rule for the UI: Documents review hosts
// must know which ledger to show while the user is still typing, and the role changes in the form
// long before anything is saved.
//
// Therefore both variants of the rule live here, matching the server:
// - with `useDocuAi`, explicit `bookingTarget` is the current reviewer decision; upload intent is
//   only the initial origin/default and must not override the visible choice;
// - without the gate, upload intent keeps its legacy precedence;
// - an accounting restoration target remains the layout source for the detached legacy flow;
// - if neither source decides, the reviewer role supplies the legacy fallback.
// Both sides read the same three inputs (`metaData.receiptUploadAction`,
// `documentInvoiceDetails.bookingTarget`,
// `documentInvoiceDetails.reviewPayee.role`) so display and
// booking cannot drift apart. Anyone changing the rule must change BOTH places — the test pins the
// table.
import { RECEIPT_TYPES } from '@/modules/base/util/constants'

const REVIEW_PAYEE_CUSTOMER_ROLES = ['companyCustomer', 'privateCustomer']

export type ReceiptType = 'expense' | 'revenue'
export type BookingTarget = ReceiptType | 'document'

// `RECEIPT_TYPES` comes from a .js constants file and is typed as `string` there — but its two
// values ARE exactly the two booking targets ('expense' / 'revenue'), identical to what appears in
// `documentInvoiceDetails.bookingTarget`. Pin them once here so the return value is typed and a host
// can pass it without a cast both as the `type` prop and as the decision to save. Two values, one
// source.
const RECEIPT_TYPE_EXPENSE = RECEIPT_TYPES.EXPENSE as ReceiptType
const RECEIPT_TYPE_REVENUE = RECEIPT_TYPES.REVENUE as ReceiptType

export const resolveReviewReceiptType = ({
  receiptUploadAction,
  receiptAccountingRestoreTarget,
  useDocuAiEnabled = false,
  bookingTarget,
  reviewPayeeRole,
}: {
  receiptUploadAction?: string | null,
  receiptAccountingRestoreTarget?: string | null,
  useDocuAiEnabled?: boolean,
  bookingTarget?: string | null,
  reviewPayeeRole?: string | null,
}): ReceiptType => {
  // This two-state helper intentionally preserves the legacy receipt-layout precedence. The
  // three-state gated accounting decision is resolved first by `resolveReviewBookingTarget` below.
  if (useDocuAiEnabled && (receiptAccountingRestoreTarget === 'expense' || receiptAccountingRestoreTarget === 'revenue')) return receiptAccountingRestoreTarget
  if (receiptUploadAction === 'createRevenue') { return RECEIPT_TYPE_REVENUE }
  if (receiptUploadAction) { return RECEIPT_TYPE_EXPENSE }

  // Only the two known values count — everything else (including `null` = undecided) falls through
  // to the role, exactly as on the server. Do not use `bookingTarget === 'revenue' ? … : …`, because
  // that would silently turn every unknown value into an expense.
  if (bookingTarget === 'revenue') { return RECEIPT_TYPE_REVENUE }
  if (bookingTarget === 'expense') { return RECEIPT_TYPE_EXPENSE }

  return REVIEW_PAYEE_CUSTOMER_ROLES.includes(reviewPayeeRole ?? '')
    ? RECEIPT_TYPE_REVENUE
    : RECEIPT_TYPE_EXPENSE
}

// The accounting decision has a third state; the form still uses an expense/revenue layout.
export const resolveReviewBookingTarget = (input: Parameters<typeof resolveReviewReceiptType>[0] & {
  isAccountingDetached?: boolean,
}): BookingTarget => {
  if (input.useDocuAiEnabled) {
    if (input.bookingTarget === 'document' || input.bookingTarget === 'expense' || input.bookingTarget === 'revenue') {
      return input.bookingTarget
    }
  }
  return resolveReviewReceiptType(input)
}
